/** @jsx jsx */
import { h, Fragment, Component } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { jsx, css } from '@emotion/react';
import { filters } from '@searchspring/snap-toolbox';

import { useMemo } from 'preact/hooks';
import { observer } from 'mobx-react';
import classnames from 'classnames';

import {
  ThemeProvider,
  defaultTheme,
  Facet,
  FacetSlider,
  FacetGridOptions,
  FacetPaletteOptions,
  FacetListOptions,
  FacetHierarchyOptions,
  FilterSummary,
  StoreProvider,
  withStore,
  withController,
  ControllerProvider,
  CacheProvider,
} from '@searchspring/snap-preact-components';

@observer
export class Sidebar extends Component {
  render() {
    const store = this.props.controller.store;

    return (
      <ThemeProvider theme={defaultTheme}>
        <ControllerProvider controller={this.props.controller}>
          <StoreProvider store={store}>
            <SidebarContents />
          </StoreProvider>
        </ControllerProvider>
      </ThemeProvider>
    );
  }
}

@withController
@withStore
@observer
export class SidebarContents extends Component {
  constructor(props) {
    super(props);
    this.keyboardHandlers = {}; // Store handlers for cleanup
    this.handlerKey = 0; // Generate unique keys
    this.slideoutObserver = null; // MutationObserver for slideout state
  }
  
  
  componentDidMount() {
    // Set up observer to watch for slideout opening
    this.setupSlideoutObserver();
    this.setupMobileSortKeySupport();
  }
  
  componentDidUpdate() {
    // Re-setup observer if needed (in case DOM structure changed)
    if (!this.slideoutObserver) {
      this.setupSlideoutObserver();
    }
  }

  componentWillUnmount() {
    // Clean up event listeners
    Object.keys(this.keyboardHandlers).forEach(key => {
      const { element, handler } = this.keyboardHandlers[key];
      if (element && handler) {
        element.removeEventListener('keydown', handler);
      }
    });
    this.keyboardHandlers = {};
    
    // Clean up observer
    if (this.slideoutObserver) {
      this.slideoutObserver.disconnect();
      this.slideoutObserver = null;
    }
  }

  setupMobileSortKeySupport() {
    // Button-like div for sorting dropdown
    const sortButton = document.querySelector('.mobile-sort-by .ss__select__dropdown__button');
    if (!sortButton) return;
  
    // Ensure it has role + tabindex for accessibility
    sortButton.setAttribute('role', 'button');
    if (!sortButton.hasAttribute('tabindex')) {
      sortButton.setAttribute('tabindex', '0');
    }
  
    // Add keyboard support: Enter & Space trigger click
    const handler = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        sortButton.click();
      }
    };
  
    sortButton.addEventListener('keydown', handler);
  
    // Store cleanup
    const key = `mobile-sort-${this.handlerKey++}`;
    this.keyboardHandlers[key] = { element: sortButton, handler };

    const sortOptions = document.querySelectorAll('.mobile-sort-by .ss__select__select__option');

    sortOptions.forEach(opt => {
      opt.setAttribute('tabindex', '0');
      opt.setAttribute('role', 'option');
      const handler = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          opt.click();
        }
      };
      opt.addEventListener('keydown', handler);

      const key = `mobile-sort-opt-${this.handlerKey++}`;
      this.keyboardHandlers[key] = { element: opt, handler };
    });
  }

  setupSlideoutObserver() {
    // Find the slideout element - use longer timeout to ensure DOM is ready
    setTimeout(() => {
      const sidebarContainer = document.querySelector('.ss-sidebar-container');
      if (!sidebarContainer) {
        console.log('Sidebar container not found, retrying...');
        // Retry after a longer delay
        setTimeout(() => this.setupSlideoutObserver(), 200);
        return;
      }

      const slideout = sidebarContainer.closest('.ss__slideout');
      if (!slideout) {
        console.log('Slideout not found');
        return;
      }

      console.log('Found slideout, setting up observer');

      // Create observer to watch for class changes
      this.slideoutObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const isActive = slideout.classList.contains('ss__slideout--active');
            console.log('Slideout state changed, isActive:', isActive);
            if (isActive) {
              // Slideout just opened, make facets focusable
              console.log('Opening - making facets focusable');
              this.ensureFacetHeadersFocusable();
            } else {
              // Slideout closed, remove focusable attributes
              console.log('Closing - removing focusable attributes');
              this.removeFacetHeadersFocusable();
            }
          }
        });
      });

      // Start observing
      this.slideoutObserver.observe(slideout, {
        attributes: true,
        attributeFilter: ['class']
      });

      // Check current state and apply appropriate tab order
      const isCurrentlyActive = slideout.classList.contains('ss__slideout--active');
      console.log('Initial slideout state - isActive:', isCurrentlyActive);
      
      if (isCurrentlyActive) {
        // Already open, make facets focusable
        this.ensureFacetHeadersFocusable();
      } else {
        // Closed, remove from tab order (this is the initial state usually)
        console.log('Setting initial state - removing from tab order');
        this.removeFacetHeadersFocusable();
      }
    }, 300);
  }
  
  ensureFacetHeadersFocusable() {
    // Use setTimeout to ensure DOM is ready
    setTimeout(() => {
      // 1. Restore X close button
      const closeButton = document.querySelector('.slideout-close-button');
      if (closeButton && closeButton.getAttribute('tabindex') === '-1') {
        console.log('Restoring close button to tab order');
        closeButton.removeAttribute('tabindex');
      }
      
      // 2. Restore View Results button
      const viewResultsButton = document.querySelector('.slideout-view-results button');
      if (viewResultsButton && viewResultsButton.getAttribute('tabindex') === '-1') {
        console.log('Restoring view results button to tab order');
        viewResultsButton.removeAttribute('tabindex');
      }
      
      const sidebarContainer = document.querySelector('.ss-sidebar-container');
      if (!sidebarContainer) return;

      
      // Find the entire slideout content area (not just sidebar-container)
      const slideoutContent = sidebarContainer.closest('.ss__slideout__content');
      const targetContainer = slideoutContent || sidebarContainer;

      // First, restore all focusable elements in slideout to tab order
      const allFocusableElements = targetContainer.querySelectorAll(
        'a, button, input, select, textarea, [tabindex="-1"]'
      );
      allFocusableElements.forEach(element => {
        // Safety check: ensure element is actually inside the slideout content
        // Don't affect elements outside like .mobile-sort-by
        if (!targetContainer.contains(element)) return;
        
        // Also skip if this element is part of mobile-sort-by (extra safety)
        if (element.closest('.mobile-sort-by')) return;
        
        // Restore original tabindex if it was stored
        const originalTabindex = element.getAttribute('data-original-tabindex');
        if (originalTabindex) {
          element.setAttribute('tabindex', originalTabindex);
          element.removeAttribute('data-original-tabindex');
        } else if (element.getAttribute('tabindex') === '-1') {
          // Remove tabindex=-1 to restore natural tab order
          element.removeAttribute('tabindex');
        }
      });

      // Find all facet headers and ensure they're focusable
      // Try multiple selectors to catch different rendering patterns
      const facetHeaders = targetContainer.querySelectorAll(
        '.ss__facet__header, [class*="facet__header"], [class*="facet-header"], .facet-container > button, .facet-container > summary'
      );
      

      facetHeaders.forEach(header => {
        // Skip if already a button or link (they're naturally focusable)
        if (header.tagName === 'BUTTON' || header.tagName === 'A' || header.tagName === 'SUMMARY') {
          // Ensure buttons and summaries are in tab order (remove negative tabindex if present)
          if (header.getAttribute('tabindex') === '-1') {
            header.removeAttribute('tabindex');
          }
          return;
        }
        
        // For divs and other elements, make them focusable
        if (header.tagName === 'DIV' || header.tagName === 'SPAN') {
          if (header.getAttribute('tabindex') !== '0') {
            header.setAttribute('tabindex', '0');
          }
          if (!header.hasAttribute('role')) {
            header.setAttribute('role', 'button');
          }
          // Add keyboard handler if it doesn't have one
          const handlerKey = header.getAttribute('data-handler-key');
          if (!handlerKey) {
            const handler = (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                header.click();
              }
            };
            header.addEventListener('keydown', handler);
            const key = `handler_${this.handlerKey++}`;
            header.setAttribute('data-handler-key', key);
            this.keyboardHandlers[key] = { element: header, handler };
          }
        }
      });

      
      // Also ensure single value toggle facets are focusable
      const singleValueFacets = targetContainer.querySelectorAll('.ss__single-value-facet a');
      singleValueFacets.forEach(link => {
        if (link.getAttribute('tabindex') === '-1') {
          link.removeAttribute('tabindex');
        }
      });
    }, 100);
  }

  removeFacetHeadersFocusable() {
    // Clean up focusable attributes when slideout closes
    // No setTimeout needed here as it's called after DOM is ready
    console.log('removeFacetHeadersFocusable called');
    
    // First, ensure mobile-sort-by is focusable and in tab order
    const mobileSortByButton = document.querySelector('.mobile-sort-by button, .mobile-sort-by .ss__select__dropdown__button, .mobile-sort-by .ss__button');
    if (mobileSortByButton) {
      console.log('Ensuring mobile-sort-by is focusable, current tabindex:', mobileSortByButton.getAttribute('tabindex'));
      // Buttons are naturally focusable, but ensure no tabindex=-1
      if (mobileSortByButton.getAttribute('tabindex') === '-1') {
        console.log('Removing tabindex=-1 from mobile-sort-by button');
        mobileSortByButton.removeAttribute('tabindex');
      }
    } else {
      console.log('Mobile sort by button not found!');
    }
    
    // Find all the slideout elements we need to remove from tab order
    // 1. X close button
    const closeButton = document.querySelector('.slideout-close-button');
    if (closeButton) {
      console.log('Setting tabindex=-1 on close button');
      closeButton.setAttribute('tabindex', '-1');
    }
    
    // 2. View Results button
    const viewResultsButton = document.querySelector('.slideout-view-results button');
    if (viewResultsButton) {
      console.log('Setting tabindex=-1 on view results button');
      viewResultsButton.setAttribute('tabindex', '-1');
    }

    // 3. All content inside sidebar container
    const sidebarContainer = document.querySelector('.ss-sidebar-container');
    if (!sidebarContainer) {
      console.log('No sidebar container found');
      return;
    }

    // Find the entire slideout content area (not just sidebar-container)
    const slideoutContent = sidebarContainer.closest('.ss__slideout__content');
    const targetContainer = slideoutContent || sidebarContainer;
    console.log('Target container:', targetContainer);

    // Find all facet headers
    const facetHeaders = targetContainer.querySelectorAll(
      '.ss__facet__header, [class*="facet__header"], [class*="facet-header"], .facet-container > button, .facet-container > summary'
    );

    facetHeaders.forEach(header => {
      // For divs and spans, remove the focusable attributes we added
      if (header.tagName === 'DIV' || header.tagName === 'SPAN') {
        // Remove keyboard handler
        const handlerKey = header.getAttribute('data-handler-key');
        if (handlerKey && this.keyboardHandlers[handlerKey]) {
          const { handler } = this.keyboardHandlers[handlerKey];
          if (handler) {
            header.removeEventListener('keydown', handler);
          }
          delete this.keyboardHandlers[handlerKey];
        }
        
        // Remove attributes and set tabindex to -1 to remove from tab order
        header.setAttribute('tabindex', '-1');
        header.removeAttribute('role');
        header.removeAttribute('data-handler-key');
      }
    });

    // Remove all focusable elements inside the entire slideout from tab order
    // This ensures when closed, tab goes: filters button -> mobile-sort-by (outside slideout) -> products
    const allFocusableElements = targetContainer.querySelectorAll(
      'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    console.log('Found focusable elements in slideout:', allFocusableElements.length);
    console.log('Target container:', targetContainer.className);
    
    let skipped = 0;
    let modified = 0;
    
    allFocusableElements.forEach(element => {
      // Safety check: ensure element is actually inside the slideout content
      // Don't affect elements outside like .mobile-sort-by or .show-filters-button
      if (!targetContainer.contains(element)) {
        console.log('Skipping - not in container:', element.className);
        skipped++;
        return;
      }
      
      // Also skip if this element is part of mobile-sort-by (extra safety)
      if (element.closest('.mobile-sort-by')) {
        console.log('Skipping - part of mobile-sort-by:', element.className);
        skipped++;
        return;
      }
      
      console.log('Setting tabindex=-1 on:', element.className, element.tagName);
      modified++;
      
      // Store original tabindex if it had one
      if (!element.hasAttribute('data-original-tabindex')) {
        const currentTabindex = element.getAttribute('tabindex');
        if (currentTabindex && currentTabindex !== '-1') {
          element.setAttribute('data-original-tabindex', currentTabindex);
        }
      }
      element.setAttribute('tabindex', '-1');
    });
    console.log(`Done - Modified: ${modified}, Skipped: ${skipped}`);
  }

  render() {
    const { filters, facets } = this.props.store;

    const singleValueToggleFacets = useMemo(() => {
      return facets.filter(f => {
        const singleValueToggleFacetNames = window.ssConfig?.singleValueFacets?.split(',');
        return singleValueToggleFacetNames?.includes(f.field);
      });
    }, [facets]);

    const nonSingleValueToggleFacets = useMemo(() => {
      return facets.filter(f => !singleValueToggleFacets.includes(f));
    }, [singleValueToggleFacets]);

    return (
      <div class='ss-sidebar-container'>
        <FilterSummary filters={filters} controller={this.props.controller} />

        <div class='ss__facets'>
          <div class='ss__facet-single-value-wrapper'>
            {singleValueToggleFacets.map((facet, index) => (
              <SingleValueToggleFacet key={facet.field || index} facet={facet} />
            ))}
          </div>

          {nonSingleValueToggleFacets.map((facet, index) => {
            return facet.display === 'none' ? null : (
              <Facet key={facet.field || index} facet={facet} optionsSlot={<OptionsSlot />} disableOverflow limit={50} />
            );
          })}
        </div>
      </div>
    );
  }
}

const SingleValueToggleFacet = ({ facet }) => {
  const value = facet.values[0];

  return (
    <CacheProvider>
      <div className='ss__single-value-facet'>
        <a
          className={classnames('ss__facet-grid-options__option', {
            'ss__facet-grid-options__option--filtered': value.filtered,
          })}
          href={value.url?.link?.href}
          onClick={e => {
            value.url?.link?.onClick(e);
          }}
        >
          {/* Use facet label for the value, since the actual value is "true" */}
          <span className='ss__facet-grid-options__option__value'>{facet.label}</span>
        </a>
      </div>
    </CacheProvider>
  );
};

const OptionsSlot = ({ facet, limit }) => {
  const { label } = facet;

  const optionHelper = useMemo(() => {
    const optionHelperJSONString = document.getElementById('optionHelpers')?.textContent;
    const optionHelpers = optionHelperJSONString ? JSON.parse(optionHelperJSONString) : {};
    return optionHelpers[label.toLowerCase()]?.valueHelper;
  }, [label]);

  const facetDisplay = useMemo(() => {
    switch (facet.display) {
      case 'grid': {
        const hasLongLabels = facet.values.some(v => v.label.length > 4);
        const columns = hasLongLabels ? 2 : 5;
        const className = `ss__facet-grid-options--${hasLongLabels ? 'long-labels' : 'short-labels'}`;
        return <FacetGridOptions values={facet.values} columns={columns} className={className} />;
      }
      case 'palette':
        return <CustomFacetPaletteOptions facet={facet} />;
      case 'hierarchy':
        return <FacetHierarchyOptions values={facet.values} />;
      case 'slider':
        return <FacetSlider facet={facet} />;
      default:
        return <FacetListOptions values={facet.values} />;
    }
  }, [facet]);

  return (
    <>
      {facetDisplay && <div className='ss__facet-options-display'>{facetDisplay}</div>}
      {optionHelper && <OptionHelper html={optionHelper} />}
    </>
  );
};

// https://codingshower.com/how-to-execute-rendered-script-tags-with-dangerouslysetinnerhtml-in-react/
const OptionHelper = ({ html }) => {
  const divRef = useRef();

  useEffect(() => {
    const fragment = document.createRange().createContextualFragment(html);
    divRef.current.append(fragment);
  }, []);

  return <div className='ss__option-helper' ref={divRef} />;
};

const CustomFacetPaletteOptions = ({ facet }) => {
  const { values } = facet;

  const transformedValues = useMemo(() => {
    return values.filter(v => v.label.toLowerCase() in window.swatches);
  }, [values]);

  const facetCss = useMemo(() => {
    const styles = {};

    transformedValues.forEach(v => {
      styles[`& .ss__facet-palette-options__option__palette--${filters.handleize(v.label)}`] = {
        background: window.swatches?.[v.label.toLowerCase()],
        display: 'block',
      };
    });

    return [css(styles)];
  }, [transformedValues]);

  return <FacetPaletteOptions css={facetCss} values={transformedValues} columns={5} hideIcon gapSize='4px' />;
};
